// RecipeGenerator.tsx
import { AtLeastOne } from '@app/utils/types';
import { Icons } from '@shared/components/content/icons';
import { Button } from '@shared/components/ui/button';
import { toast } from '@shared/components/ui/use-toast';
import { cn } from '@shared/lib/utils';
import { useGenerateRecipe } from '@shared/reggie/api/useGenerateRecipe';
import sentry from '@shared/services/sentry';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface RecipeGeneratorPropsBase {
  /** Called when a recipe has been successfully generated */
  onGenerated: (generated: GeneratedRecipe) => void;
  /** Optional user UUID (used in ProductForm) */
  userUuid?: string;
  /** Called whenever loading state changes */
  onLoadingChange: (loading: boolean) => void;
  /** Called when an error occurs */
  onError?: (error: unknown) => void;
  /** Optional label override for input */
  label?: string;
  className?: string;

  recipeName?: string | null;
  formName?: string;
}

type RecipeGeneratorProps = AtLeastOne<
  RecipeGeneratorPropsBase,
  'recipeName' | 'formName'
>;

export default function RecipeGenerator({
  onGenerated,
  userUuid,
  onLoadingChange,
  onError,
  recipeName,
  formName,
  className,
}: RecipeGeneratorProps) {
  const { mutateAsync: generateRecipe } = useGenerateRecipe();
  const [loading, setLoading] = useState(false);

  const { trigger, getValues } = useFormContext();

  if (!recipeName && !formName) {
    throw new Error(
      'RecipeGenerator requires either recipeName or formName to be provided'
    );
  }

  // Notify parent when loading changes
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const runGenerate = async () => {
    let recipeNameFromForm;

    if (formName) {
      const formNameValid = await trigger(formName);
      if (!formNameValid) return;

      recipeNameFromForm = getValues(formName!);
    }

    if (recipeName && recipeName.trim().length !== 0 && recipeName.length < 3) {
      toast({
        title: 'Invalid recipe name',
        description:
          'Please enter a valid recipe name with at least 3 characters.',
        variant: 'destructive',
      });
      return;
    }

    const name = recipeNameFromForm || recipeName;

    setLoading(true);
    try {
      const generated = await generateRecipe({
        name: name,
        user_uuid: userUuid,
      });
      if (!generated.ingredients?.length) {
        toast({
          title: 'No recipe could be generated',
          description: 'Please check your recipe name and try again.',
          variant: 'destructive',
        });
        return;
      }
      onGenerated({ name: recipeName, ...generated });
    } catch (error) {
      toast({
        title: 'Failed to generate recipe',
        description: 'Please try again later.',
        variant: 'destructive',
      });
      sentry.log(error);
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn('space-y-2')}>
      <Button
        type="button"
        size="lg"
        variant="secondary"
        className={className}
        loading={loading}
        disabled={loading}
        icon={<Icons.wand2 className="size-4" />}
        onClick={runGenerate}
      >
        Generate recipe
      </Button>
      {/* {loading && (
        <div className="flex justify-center pt-4">
          <ReggieLoader />
        </div>
      )} */}
    </div>
  );
}
